import { useQuery } from "react-query";
import { useMemo } from "react";
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from "../../../api";
import React from "react";

const smartTableApi = new ApiRequest(API_ROUTES.SMART_TABLE, API_AUTH_TYPES.ADVISOR);

/**
 * Fetches the complete smart table data including all rows and all view configurations.
 * This hook only depends on tableId and caches everything, allowing instant view switching.
 * Heavily optimized to respect prefetched data and avoid unnecessary refetches.
 */
const useFetchSmartTableData = (tableId, selectedViewId) => {
    const {
        data,
        isLoading,
        isError,
        isFetching,
        refetch
    } = useQuery(
        ["smart-table-complete-data", { tableId }], // Cache key depends only on tableId
        async () => {
            console.log(`🚀 FETCHING COMPLETE TABLE DATA for table: ${tableId} (initial view: ${selectedViewId || 'default'})`);
            // Use the existing endpoint - the view ID is just for the initial fetch
            // but we get all views in the response, so view switching becomes client-side only
            const response = await smartTableApi.get({
                endpoint: "table",
                params: [tableId, selectedViewId || "default", 0, true], // Use "default" if no selectedViewId
            });

            console.log(`✅ FETCHED ${tableId}: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        {
            staleTime: 20 * 60 * 1000, // 5 minutes - don't refetch unless data is older than this
            cacheTime: 25 * 60 * 1000, // 15 minutes - keep in cache even longer
            keepPreviousData: true,
            enabled: !!tableId, // Only depends on tableId, selectedViewId has fallback
            // Aggressive caching settings to respect prefetched data
            refetchOnMount: false, // Don't refetch on mount if data is still fresh
            refetchOnWindowFocus: false, // Don't refetch when window regains focus
            refetchOnReconnect: false, // Don't refetch on network reconnect
            // Only refetch if data is truly stale or on manual refetch
            notifyOnChangeProps: ['data', 'isError'], // Only notify on data/error changes, not loading states
        }
    );

    // Log when we're using cached data vs actually fetching
    React.useEffect(() => {
        if (data && !isFetching) {
            console.log(`💾 Using CACHED data for table: ${tableId} (${data.rows?.length} rows)`);
        }
    }, [data, isFetching, tableId]);

    return {
        allRows: data?.rows || [],
        metadata: data?.metadata || null,
        isLoading,
        isError,
        isFetching,
        refetch
    };
};

/**
 * Hook to get view-specific metadata from the cached all_views data.
 * This is purely client-side - no API calls when switching views!
 * Uses proper memoization to prevent unnecessary rerenders.
 */
const useSmartTableViewMetadata = (metadata, selectedViewId) => {
    return useMemo(() => {
        if (!metadata?.all_views || !selectedViewId) {
            return null;
        }

        console.log(`⚡ INSTANT view switch (client-side): ${selectedViewId}`);

        // Find the selected view from the all_views array (client-side only!)
        const selectedView = metadata.all_views.find(view => view.smart_table_view_id === selectedViewId)
            || metadata.all_views[0]; // fallback to first view

        if (!selectedView) {
            return null;
        }

        // Return the view-specific configuration (memoized to prevent rerenders)
        return {
            filters: selectedView.filters || [],
            sorts: selectedView.sorts || [],
            hidden_columns: selectedView.hidden_columns || [],
            column_order: selectedView.column_order || [],
            column_widths: selectedView.column_widths || {},
            conditional_formatting: selectedView.conditional_formatting || [],
            grouping: selectedView.grouping || [],
            summary_preferences: selectedView.summary_preferences || {},
            view_id: selectedView.smart_table_view_id,
            view_name: selectedView.name,
            all_views: metadata.all_views,
            columns: metadata.columns,
            table_name: metadata.table_name,
        };
    }, [metadata, selectedViewId]); // Only recalculate when metadata or selectedViewId changes
};

export default useFetchSmartTableData;
export { useSmartTableViewMetadata }; 